import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
// Rxjs
import { Observable , of} from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
// Environment
import { environment } from 'src/environments/environment';
// Model
import { MessageType } from '../components/messages/message-handler/message-handler.component';
import { MessageHandlerService } from '../components/messages/message-handler/message-handler.service';
import { SaveType } from '../core/models/enumerations/save-type';
import { CompanyOwners, Plants } from '../core/models/index';
// Interface
import { TranslateService } from '@ngx-translate/core';
import { Cacheable, LocalStorageStrategy } from 'ts-cacheable';
import { LoadWheelService } from '../components/load-wheel/load-wheel.service';
import { Areas, RelatedEntityResult, Units } from '../core/models/common';
import { EntityName } from '../core/models/enumerations/entity-name';
import { MiningEquipmentSearch, MiningEquipmentSearchAdapter, MiningEquipmentType } from '../core/models/mining-equipment';
import { MiningEquipmentDisplay } from '../core/models/miningequipment/display/mining-equipment-display';
import { SpinnerProcess } from '../core/models/spinner-process';
import { RecordLockingFlow } from '../core/record-locking/record-locking-flow';
import { EntityCommonService } from '../core/services/entity-common.service';
import { LockSameUserMessageService, LockSameUserResult } from '../core/services/lock-same-user-message.service';
import { TelemetryService } from '../core/services/telemetry.service';
import { EntityUtilsService } from '../core/utils/entity-utils.service';

@Injectable({
    providedIn: 'root'
})
export class MiningEquipmentSectionService {

    constructor(
        private readonly recordLockingFlow: RecordLockingFlow,
        private readonly http: HttpClient,
        private readonly messageHandlerService: MessageHandlerService,
        private readonly loadWheelService: LoadWheelService,
        private readonly entityCommonService: EntityCommonService,
        private readonly translate: TranslateService,
        private readonly miningEquipmentSearchAdapter: MiningEquipmentSearchAdapter,
        private readonly entityUtilsService: EntityUtilsService,
        private readonly lockSameUserMessageService: LockSameUserMessageService,
        private readonly telemetry: TelemetryService,
    ) { }

    getMiningEquipmentDetailsById(miningEquipmentId: string, isReload: boolean = false, quickAccess: boolean = false): Promise<MiningEquipmentDisplay> {
        let mainLockID: number = 0;

        if (isReload) {
            mainLockID = this.recordLockingFlow.getLockID(EntityName.MEQUIPMENT, miningEquipmentId);
        }

        return new Promise<MiningEquipmentDisplay>((resolve) => {
            this.lockSameUserMessageService.validateEntityIsLockedByUser(this.recordLockingFlow, EntityName.MEQUIPMENT, miningEquipmentId, mainLockID, quickAccess).then((lockSameUserResult: LockSameUserResult) => {
                if(lockSameUserResult.openEntity) {
                    this.getEntityDetailsById(miningEquipmentId, mainLockID, false).subscribe((entityDisplay: MiningEquipmentDisplay) => {
                        resolve(entityDisplay);
                    });
                } else {
                    resolve(MiningEquipmentDisplay.BuildMiningEquipmentDisplay({}));
                }
            });
        });
    }

    private getEntityDetailsById(miningEquipmentId: string, mainLockID: number, onlyForRead: boolean): Observable<MiningEquipmentDisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.miningEquipment") + ' (' + this.translate.instant("loading.mainRecord") + ')');
        return this.http.get<MiningEquipmentDisplay>(`${environment.apiUrl}miningEquipment/${miningEquipmentId}/${mainLockID}/${onlyForRead}`)
            .pipe(
                map((data: any) => {
                    let equipmentDisplay = MiningEquipmentDisplay.BuildMiningEquipmentDisplay({});
                    if (data.response) {
                        equipmentDisplay = MiningEquipmentDisplay.BuildMiningEquipmentDisplay(data.response);

                        if(onlyForRead){
                            if(equipmentDisplay.miningEquipmentId && !equipmentDisplay.lockError){
                                this.recordLockingFlow.setLockItem(EntityName.MEQUIPMENT, equipmentDisplay.miningEquipmentId, equipmentDisplay.lockId, equipmentDisplay.lockMode);
                                this.recordLockingFlow.clearLockFlow(EntityName.MEQUIPMENT, miningEquipmentId);
                            }
                        }else if (equipmentDisplay.miningEquipmentId) {
                            if (equipmentDisplay.hasDiffUserLockError()) {
                                this.messageHandlerService.show(equipmentDisplay.lockError, MessageType.INFO);
                            }
    
                            // Set the locking info into lock global list.
                            this.recordLockingFlow.setLockItem(EntityName.MEQUIPMENT, equipmentDisplay.miningEquipmentId, equipmentDisplay.lockId, equipmentDisplay.lockMode);
                            this.recordLockingFlow.initLockRefreshTimer(EntityName.MEQUIPMENT, equipmentDisplay.miningEquipmentId, equipmentDisplay.lockId);
    
                        } else {
                            this.messageHandlerService.show(equipmentDisplay.lockError, MessageType.ERROR);
                        }
                    } else {
                        const errorType = MessageHandlerService.errorType(data);
                        if (errorType === MessageType.INFO) {
                            this.entityCommonService.sendEntityNotFoundEvent();
                        } else {
                            this.messageHandlerService.show(MessageHandlerService.errorMessage(data), errorType);
                        }
                    }
                    return equipmentDisplay;
                }),
                catchError((error) => {
                    this.loadWheelService.hideWheel(wheel);
                    return of(MiningEquipmentDisplay.BuildMiningEquipmentDisplay({}));
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    getMiningEquipmentDetailsByIdForRead(miningEquipmentId: string): Observable<MiningEquipmentDisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.miningEquipment") + ' (' + this.translate.instant("loading.mainRecord") + ')');
        return this.http.get<MiningEquipmentDisplay>(`${environment.apiUrl}miningEquipment/getMiningEquipmentDetailsByIdForRead/${miningEquipmentId}`)
            .pipe(
                map((data: any) => {
                    let equipmentDisplay = MiningEquipmentDisplay.BuildMiningEquipmentDisplay({});
                    if (data.response) {
                        equipmentDisplay = MiningEquipmentDisplay.BuildMiningEquipmentDisplay(data.response);
                    } else {
                        const errorType = MessageHandlerService.errorType(data);
                        if (errorType === MessageType.INFO) {
                            this.entityCommonService.sendEntityNotFoundEvent();
                        } else {
                            this.messageHandlerService.show(MessageHandlerService.errorMessage(data), errorType);
                        }
                    }
                    return equipmentDisplay;
                }),
                catchError((error) => {
                    return of(MiningEquipmentDisplay.BuildMiningEquipmentDisplay({}));
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    getMiningEquipmentById(miningEquipmentId: number): Observable<MiningEquipmentSearch> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.miningEquipment") + ' (' + this.translate.instant("loading.details") + ')');
        return this.http.get<MiningEquipmentSearch>(`${environment.apiUrl}miningEquipment/${miningEquipmentId}`)
            .pipe(
                map((data: any) => {
                    return this.miningEquipmentSearchAdapter.adapt(data.response[0]);
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    saveMiningEquipmentDetails(equipmentDisplay: MiningEquipmentDisplay): Observable<MiningEquipmentSearch> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("saving.mequipment"));
        equipmentDisplay = this.entityUtilsService.prepareEntityForSave(equipmentDisplay) as MiningEquipmentDisplay;
        return this.http.put<MiningEquipmentSearch>(`${environment.apiUrl}miningEquipment`, equipmentDisplay)
            .pipe(
                map((data: any) => {
                    if (data.response === null) {
                        if (data.exception !== null) {
                            this.messageHandlerService.showExceptionDialog(data);
                        } else if (data.message !== null) {
                            if (equipmentDisplay.saveType === SaveType.TYPE_QC || equipmentDisplay.saveType === SaveType.UPDATE) {
                                //When the user attempts to QC a record while the lock was already cleared,
                                //this//the above warning message should still be thrown; however, the record should not reload.
                                if (data.messageType !== MessageType.WARNING.valueOf()) {
                                    this.entityCommonService.sendReloadEvent();
                                }
                            } else {
                                equipmentDisplay.intDataDepMigError = data.message;
                            }

                        }
                    } else {
                        const isQcUpdateOrAmend = equipmentDisplay.saveType === SaveType.TYPE_QC ||
                            equipmentDisplay.saveType === SaveType.AMENDMENT ||
                            equipmentDisplay.saveType === SaveType.UPDATE;

                        const afterClearFunc = () => this.entityCommonService.sendPreReleaseEvent();
                        this.recordLockingFlow.clearLockFlow(EntityName.MEQUIPMENT, String(equipmentDisplay.miningEquipmentId), isQcUpdateOrAmend, afterClearFunc);
                    }
                    return data.response;
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    miningEquipmentNextId(): Observable<MiningEquipmentDisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.nextId"));
        return this.http.get<any>(`${environment.apiUrl}miningEquipment/nextId`)
            .pipe(
                map((data: any) => {
                    const equipmentDisplay = MiningEquipmentDisplay.BuildNewMiningEquipment(data.response);
                    if (equipmentDisplay?.miningEquipmentId) {
                        // Set the locking info into lock global list.
                        this.recordLockingFlow.setLockItem(EntityName.MEQUIPMENT, equipmentDisplay.miningEquipmentId, -1);
                    }
                    return equipmentDisplay;
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    @Cacheable({
        storageStrategy: LocalStorageStrategy
    })
    getMiningEquipmentTypes(): Observable<MiningEquipmentType[]> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("common.loading") + ' ' + this.translate.instant("mEquipment.searchMiningEquipment.mEquipmentType"));
        return this.http.get<MiningEquipmentType>(`${environment.apiUrl}miningEquipment/miningEquipmentTypes`)
            .pipe(
                map((data: any) => MiningEquipmentType.BuildMiningEquipmentType(data.response)),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    searchMiningEquipments(httpParams: HttpParams): Observable<MiningEquipmentSearch[]> {
        const searchStart: number = performance.now();

        return this.http.get<MiningEquipmentSearch[]>(`${environment.apiUrl}miningEquipment/search`, { params:httpParams })
            .pipe(
                map((data: any) => {
                    let equipment: Array<MiningEquipmentSearch> = data.response;
                    this.entityUtilsService.verifyMaxResult(data);
                    this.telemetry.entitySearchTime("mequipment", performance.now() - searchStart);
                    return equipment.map(item => this.miningEquipmentSearchAdapter.adapt(item));
                })
            );
    }

    loadMiningEquimentAreas(miningEquipmentId: number): Observable<RelatedEntityResult<Areas>> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("common.loading") + ' ' + this.translate.instant("relatedInfo.common.related") + ' ' + this.translate.instant("relatedInfo.area.title"));
        return this.http.get<RelatedEntityResult<Areas>>(`${environment.apiUrl}miningEquipment/areas/${miningEquipmentId}`)
            .pipe(
                map((data: any) => RelatedEntityResult.Build(data.response, Areas.BuildAreas)),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    loadMiningEquimentOwners(miningEquipmentId: number): Observable<RelatedEntityResult<CompanyOwners>> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("common.loading") + ' ' + this.translate.instant("relatedInfo.common.related") + ' ' + this.translate.instant("mEquipment.owners.title"));
        return this.http.get<RelatedEntityResult<CompanyOwners>>(`${environment.apiUrl}miningEquipment/owners/${miningEquipmentId}`)
            .pipe(
                map((data: any) => RelatedEntityResult.Build(data.response, CompanyOwners.BuildCompOwners)),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    loadMiningEquimentVendors(miningEquipmentId: number): Observable<RelatedEntityResult<CompanyOwners>> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("common.loading") + ' ' + this.translate.instant("relatedInfo.common.related") + ' ' + this.translate.instant("mEquipment.vendors.title"));
        return this.http.get<RelatedEntityResult<CompanyOwners>>(`${environment.apiUrl}miningEquipment/vendors/${miningEquipmentId}`)
            .pipe(
                map((data: any) => RelatedEntityResult.Build(data.response, CompanyOwners.BuildCompOwners)),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    loadMiningEquimentPlants(miningEquipmentId: number): Observable<RelatedEntityResult<Plants>> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("common.loading") + ' ' + this.translate.instant("relatedInfo.common.related") + ' ' + this.translate.instant("relatedInfo.plant.title"));
        return this.http.get<RelatedEntityResult<Plants>>(`${environment.apiUrl}miningEquipment/plants/${miningEquipmentId}`)
            .pipe(
                map((data: any) => RelatedEntityResult.Build(data.response, Plants.BuildPlants)),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    loadMiningEquimentUnits(miningEquipmentId: number): Observable<RelatedEntityResult<Units>> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("common.loading") + ' ' + this.translate.instant("relatedInfo.common.related") + ' ' + this.translate.instant("relatedInfo.unit.title"));
        return this.http.get<RelatedEntityResult<Units>>(`${environment.apiUrl}miningEquipment/units/${miningEquipmentId}`)
            .pipe(
                map((data: any) => RelatedEntityResult.Build(data.response, Units.BuildUnits)),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }
    
}


